/** Canonical SOP step variant vocabulary. */

export const SOP_STEP_ARCHETYPE_SPECIAL_ACTION = 'SPECIAL_ACTION';
export const SOP_STEP_ARCHETYPE_BINARY_CHOICE = 'BINARY_CHOICE';
export const SOP_STEP_ARCHETYPE_TERMINAL = 'TERMINAL';
/** Legacy overlay / incident_data stepType for a completed terminal. */
export const SOP_STEP_TYPE_END_SOP = 'END_SOP';
export const SOP_STEP_VARIANT_VOICEDOWN = 'voicedown';
export const SOP_STEP_VARIANT_GUARD_DISPATCH = 'guard_dispatch';
export const SOP_STEP_VARIANT_VOICEDOWN_CAPABILITY = 'voicedown_capability';

export const EMERGENCY_CONTACT_VARIANTS = [
  'emergency_police',
  'emergency_fire',
  'emergency_security',
] as const;

export const CALL_LIST_CONTACT_VARIANTS = [
  'call_list',
  'call_owner_list',
  'call_group',
  'call',
] as const;

export type EmergencyContactVariant = (typeof EMERGENCY_CONTACT_VARIANTS)[number];
export type CallListContactVariant = (typeof CALL_LIST_CONTACT_VARIANTS)[number];

export function isEmergencyContactVariant(variant: string | null | undefined): boolean {
  const v = String(variant || '').trim();
  return (EMERGENCY_CONTACT_VARIANTS as readonly string[]).includes(v);
}

export function isCallListContactVariant(variant: string | null | undefined): boolean {
  const v = String(variant || '').trim();
  return (CALL_LIST_CONTACT_VARIANTS as readonly string[]).includes(v);
}

export function isCanonicalVoicedownStep(
  archetype: string | null | undefined,
  variant: string | null | undefined,
): boolean {
  return (
    String(archetype || '').trim() === SOP_STEP_ARCHETYPE_SPECIAL_ACTION &&
    String(variant || '').trim() === SOP_STEP_VARIANT_VOICEDOWN
  );
}

/** BINARY_CHOICE step where the operator declares whether the site can voice down. */
export function isCanonicalVoicedownCapabilityStep(
  archetype: string | null | undefined,
  variant: string | null | undefined,
): boolean {
  return (
    String(archetype || '').trim() === SOP_STEP_ARCHETYPE_BINARY_CHOICE &&
    String(variant || '').trim() === SOP_STEP_VARIANT_VOICEDOWN_CAPABILITY
  );
}

export function isCanonicalGuardDispatchStep(
  archetype: string | null | undefined,
  variant: string | null | undefined,
): boolean {
  return (
    String(archetype || '').trim() === SOP_STEP_ARCHETYPE_SPECIAL_ACTION &&
    String(variant || '').trim() === SOP_STEP_VARIANT_GUARD_DISPATCH
  );
}

/** True when any value is a TERMINAL or legacy END_SOP step type. */
export function isTerminalSopStep(...types: unknown[]): boolean {
  return types.some((value) => {
    const normalized = String(value ?? '').trim().toUpperCase();
    return normalized === SOP_STEP_ARCHETYPE_TERMINAL || normalized === SOP_STEP_TYPE_END_SOP;
  });
}

function parseStepConfig(config: unknown): Record<string, unknown> {
  if (config == null) return {};
  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof config === 'object' && !Array.isArray(config)) {
    return { ...(config as Record<string, unknown>) };
  }
  return {};
}

function parseDefinitionRequireFlag(value: unknown): boolean {
  return value === true || value === 1;
}

export type RequireSuccessfulContactDefinition = {
  requires_successful_contact?: boolean | number | null;
} | null;

/**
 * Whether operators must confirm contact was reached before advancing (Yes/No UI).
 * Call-list variants always false (flow comes from contact_lists.list_type).
 * Otherwise: explicit config override → step definition default → emergency variant fallback.
 */
export function resolveRequireSuccessfulContact(
  step: {
    archetype?: string | null;
    variant?: string | null;
    config?: unknown;
  },
  definition?: RequireSuccessfulContactDefinition,
): boolean {
  if (isCallListContactVariant(step.variant)) {
    return false;
  }
  const cfg = parseStepConfig(step.config);
  if (typeof cfg.requireSuccessfulContact === 'boolean') {
    return cfg.requireSuccessfulContact;
  }
  if (definition != null && definition.requires_successful_contact != null) {
    return parseDefinitionRequireFlag(definition.requires_successful_contact);
  }
  const archetype = String(step.archetype || '').trim();
  if (archetype === 'CONTACT_ACTION' && isEmergencyContactVariant(step.variant)) {
    return true;
  }
  return false;
}

export function definitionKey(archetype: string | null | undefined, variant: string | null | undefined): string {
  return `${String(archetype || '').trim()}:${String(variant || '').trim()}`;
}
